import { CheckCircle, Lock, Target, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export type EmptyStateType = 'no-program' | 'skipped-optional' | 'unavailable' | 'completed';

interface EmptyStateProps {
  type: EmptyStateType;
  onAction?: () => void;
  actionLabel?: string;
}

// UX-AUDIT-V3-306: Helper to get tomorrow's date formatted
const getNextSessionDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });
};

export function EmptyState({ type, onAction, actionLabel }: EmptyStateProps) {
  const config = {
    'no-program': {
      title: 'No Training Program Yet',
      description: 'Upload a swing video to get your personalized training program',
      icon: <Target className="w-6 h-6 text-[#196644]" />,
      action: actionLabel ?? 'Upload Swing Video',
    },
    'skipped-optional': {
      title: 'No Optional Drills Added',
      description: 'Focus on your required drills, or add optional drills to build your complete game',
      icon: <Target className="w-6 h-6 text-[#196644]" />,
      action: actionLabel ?? 'Add Optional Drill',
    },
    unavailable: {
      title: 'Drill Not Available Yet',
      description: 'This drill will unlock as you progress through your training program',
      icon: <Lock className="w-6 h-6 text-gray-400" />,
      action: undefined,
    },
    completed: {
      title: 'Great Work Today!',
      description: "You've completed today's training program",
      icon: <CheckCircle className="w-6 h-6 text-[#196644]" />,
      action: actionLabel ?? 'View Progress',
    },
  } as const;

  const content = config[type];

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <Card className="w-full max-w-md text-center">
        <CardContent className="p-8 space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#196644]/10 flex items-center justify-center">
            {content.icon}
          </div>
          <h2 className="text-xl text-[#0D382A] font-semibold">{content.title}</h2>
          <p className="text-sm text-gray-600">{content.description}</p>
          
          {/* UX-AUDIT-V3-306: Next session date card for completed state */}
          {type === 'completed' && (
            <div className="bg-gradient-to-r from-[#F9C94E]/20 to-[#196644]/10 border border-[#F9C94E]/30 rounded-xl p-4 mt-4">
              <div className="flex items-center justify-center gap-2 text-[#0D382A]">
                <Calendar className="w-5 h-5 text-[#196644]" />
                <span className="text-sm font-medium">Next Session</span>
              </div>
              <p className="text-lg font-semibold text-[#0D382A] mt-1">{getNextSessionDate()}</p>
            </div>
          )}
          
          {content.action && onAction && (
            <Button onClick={onAction} className="w-full" variant={type === 'completed' ? 'outline' : 'default'}>
              {content.action}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface InlineEmptyStateProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function InlineEmptyState({ title, description, icon, action }: InlineEmptyStateProps) {
  return (
    <Card className="border border-dashed border-gray-200">
      <CardContent className="p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-[#196644]/10 flex items-center justify-center">
            {icon ?? <Target className="w-4 h-4 text-[#196644]" />}
          </div>
          <div>
            <p className="text-[#0D382A] font-medium">{title}</p>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        {action && (
          <Button variant="outline" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
